import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: '',
  code: '',
  description: '',
  is_active: true,
  dp_enabled: false,
  dp_type: 'percent',
  dp_percent: 30,
  dp_value: 0,
  voucher_enabled: false,
};

const PackageTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/package-types?show_all=true`);
      setTypes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching package types:', error);
      toast.error('Gagal memuat data tipe paket');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleOpen = (type = null) => {
    if (type) {
      setEditing(type);
      setFormData({ name: type.name || '', code: type.code || '', description: type.description || '', is_active: type.is_active !== false, dp_enabled: type.dp_enabled || false, dp_type: type.dp_type || 'percent', dp_percent: type.dp_percent || 30, dp_value: type.dp_value || 0, voucher_enabled: type.voucher_enabled || false });
    } else {
      setEditing(null);
      setFormData(initialState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Nama dan kode wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, code: formData.code.trim().toLowerCase() };
      if (editing) {
        await axios.put(`${API}/master/package-types/${editing.id}`, payload);
        toast.success('Tipe paket berhasil diupdate');
      } else {
        await axios.post(`${API}/master/package-types`, payload);
        toast.success('Tipe paket berhasil ditambahkan');
      }
      setShowModal(false);
      fetchTypes();
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus tipe paket ini?')) return;
    try {
      await axios.delete(`${API}/master/package-types/${id}`);
      toast.success('Tipe paket dihapus');
      fetchTypes();
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggle = async (id, current) => {
    try {
      await axios.put(`${API}/master/package-types/${id}`, { is_active: !current });
      toast.success('Status diubah');
      fetchTypes();
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-['Outfit']">Master Tipe Paket</CardTitle>
            <CardDescription>Daftar tipe paket (Umrah, Haji, LA, dll)</CardDescription>
          </div>
          <Button className="rounded-full" onClick={() => handleOpen()}>
            <Plus className="h-4 w-4 mr-2" /> Tambah Tipe
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : types.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Belum ada tipe paket</p>
          ) : (
            <div className="grid gap-3">
              {types.map((t) => (
                <div key={t.id} className="flex items-center justify-between border rounded-xl p-4">
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{t.code}</p>
                    {t.description && <p className="text-sm text-muted-foreground mt-1">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => handleToggle(t.id, t.is_active)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${t.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                      title={t.is_active ? 'Nonaktifkan' : 'Aktifkan'}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${t.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <Button variant="outline" size="sm" onClick={() => handleOpen(t)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDelete(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Tipe Paket' : 'Tambah Tipe Paket'}</DialogTitle>
            <DialogDescription>Lengkapi form tipe paket</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Nama Tipe *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Umrah Reguler" required />
              </div>
              <div className="space-y-2">
                <Label>Kode *</Label>
                <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="umrah" required disabled={!!editing} />
                <p className="text-xs text-muted-foreground">Unik, lowercase. Tidak bisa diubah setelah dibuat.</p>
              </div>
              <div className="space-y-2">
                <Label>Deskripsi</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Keterangan tipe paket" />
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <Label>Aktif</Label>
                  <p className="text-xs text-muted-foreground">Tipe paket aktif ditampilkan di pilihan paket</p>
                </div>
                <button type="button" onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.is_active ? 'bg-primary' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* DP & Voucher Settings */}
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Aktifkan DP untuk tipe ini</Label>
                    <p className="text-xs text-muted-foreground">Semua paket dengan tipe ini bisa bayar DP</p>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, dp_enabled: !formData.dp_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.dp_enabled ? 'bg-primary' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.dp_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {formData.dp_enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/30">
                    <div className="space-y-2">
                      <Label>Tipe DP</Label>
                      <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.dp_type} onChange={(e) => setFormData({ ...formData, dp_type: e.target.value })}>
                        <option value="percent">Persen (%)</option>
                        <option value="nominal">Nominal (Rp)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>{formData.dp_type === 'nominal' ? 'Nominal DP (Rp)' : 'Persen DP (%)'}</Label>
                      <Input type="number" value={formData.dp_type === 'nominal' ? formData.dp_value : formData.dp_percent}
                        onChange={(e) => setFormData({ ...formData, [formData.dp_type === 'nominal' ? 'dp_value' : 'dp_percent']: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-4">
                  <div>
                    <Label>Aktifkan Voucher untuk tipe ini</Label>
                    <p className="text-xs text-muted-foreground">Semua paket dengan tipe ini bisa pakai voucher</p>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, voucher_enabled: !formData.voucher_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.voucher_enabled ? 'bg-primary' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.voucher_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Simpan' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackageTypesManagement;
